import APIv4MetaproductsAdapters from './APIv4MetaproductsAdapters';
const APIv4GetProductsByFiltersResponseAdapter = {
  toProductByFilters: (v4Response, metaproductColors = {}) => {
    let response = v4Response;
    let aggregations = undefined;
    if (response.aggregations) {
      aggregations = response.aggregations;
    }
    if (response.filters) {
      response = response.filters[0];
    }
    const products = response.products.map(item => {
      const {
        price_info,
        stock
      } = item;
      return {
        v4ApiResponse: item,
        categories: item.categories,
        country: item.country,
        descriptions: APIv4GetProductsByFiltersResponseAdapter.mapDescriptions(item.descriptions),
        hiddenPrice: item.price_hide,
        id: item.productID,
        imageUrl: item.image,
        price: {
          country: price_info.country || '',
          currency: price_info.currencies_list[0] || '',
          currencies: Object.keys(price_info.currencies).map(k => ({
            currency: k,
            prices: price_info.currencies[k].map(p => ({
              price: p.price,
              oldPrice: p.crossed_price ?? 0
            }))
          })),
          includesTaxes: price_info.tax_included || '',
          prices: price_info.prices,
          taxType: price_info.tax_type || ''
        },
        gallery: APIv4GetProductsByFiltersResponseAdapter.mapGallery(item),
        state: item.state,
        stock: {
          lowStockAlert: parseInt(stock.low_stock_alert) || 0,
          policy: stock.policy || 0,
          stock: stock.stock == 'INFINITE' ? Infinity : parseInt(stock.stock)
        },
        type: item.type,
        relatedProducts: APIv4GetProductsByFiltersResponseAdapter.mapRelatedProducts(item, 'crosssell'),
        recommendedProducts: APIv4GetProductsByFiltersResponseAdapter.mapRelatedProducts(item, 'upsell'),
        hasMetaproductsV4: APIv4GetProductsByFiltersResponseAdapter.hasMetaproducts(item),
        metaproductsV4: APIv4GetProductsByFiltersResponseAdapter.mapMetaproducts(item, metaproductColors),
        bundleProductsV4: item.specific.elements,
        custom_fields: APIv4GetProductsByFiltersResponseAdapter.mapCustomFields(item),
        metaproductCustomFields: APIv4GetProductsByFiltersResponseAdapter.mapMetaproductCustomFields(item),
        specificV4: item.specific,
        refID: item.refs.refID
      };
    });
    const responseAdapter = {
      total: response.total,
      products: products
    };
    if (aggregations) {
      responseAdapter.aggregations = aggregations;
    }
    return responseAdapter;
  },
  mapGallery: v4Product => {
    const gallery = [];
    if (v4Product.image) {
      gallery.push({
        name: '',
        nameAlt: '',
        type: 'file',
        url: v4Product.image,
        visible: true
      });
    }
    if (v4Product.previews_primary && v4Product.previews_primary.length > 0) {
      const mappedPrimaryPreviews = v4Product.previews_primary.map(media => ({
        name: media.name || '',
        nameAlt: media.alt_name || '',
        type: media.type,
        url: media.url || media.filename,
        visible: Boolean(media.visible) || true
      }));
      gallery.push(...mappedPrimaryPreviews);
    }
    if (v4Product.previews && v4Product.previews.length > 0) {
      const mappedPreviews = v4Product.previews.map(media => ({
        name: media.name || '',
        nameAlt: media.alt_name || '',
        type: media.type,
        url: media.url || media.filename,
        visible: Boolean(media.visible) || true
      }));
      gallery.push(...mappedPreviews);
    }
    return gallery;
  },
  mapDescriptions: productDescriptions => {
    return productDescriptions.map(description => ({
      lang: description.lang,
      title: description.title,
      short: description.short,
      long: description.long,
      seo: description.seo,
      sections: APIv4GetProductsByFiltersResponseAdapter.mapDescriptionSections(description)
    }));
  },
  mapDescriptionSections: productDescription => {
    const sections = [];
    sections.push({
      i18nTitleTag: 'PRODUCTS.DESCRIPTION',
      content: productDescription.long
    });
    if (productDescription.features !== undefined) {
      sections.push({
        i18nTitleTag: 'PRODUCTS.FEATURES',
        content: productDescription.features
      });
    }
    if (productDescription.custom_tabs !== undefined) {
      sections.push(...productDescription.custom_tabs.filter(tab => tab.content).map(tab => ({
        title: tab.title,
        content: tab.content
      })));
    }
    if (productDescription.extra_descriptions !== undefined) {
      sections.push(...productDescription.extra_descriptions.filter(description => description.extra_content && Array.isArray(description.extra_content)).map(description => ({
        title: description.extra_name,
        content: description.extra_content.map(item => item.content).join('<br>')
      })));
    }
    return sections.filter(section => section.content);
  },
  mapRelatedProducts: (product, relationType) => {
    let relations = {};
    let specific;
    if (product.specificV4) {
      specific = product.specificV4;
    } else {
      specific = product.specific;
    }
    if (relationType == 'upsell' && specific.extension.other) {
      relations = specific.extension.other;
    }
    if (relationType == 'crosssell' && product.upselling.crosssell) {
      relations = product.upselling.crosssell;
    }
    if (!relations || Object.keys(relations).length == 0) {
      return undefined;
    }
    const relatedFeedsHasContent = relations.related_feeds ?? false;
    const hasRelatedFeeds = !Array.isArray(relations.related_feeds) && relatedFeedsHasContent;
    const relatedOidsHasContent = relations.related_oids ?? false;
    const hasRelatedOids = !Array.isArray(relations.related_oids) && relatedOidsHasContent;
    const relatedRefIdsHasContent = relations.related_refids ?? false;
    const hasRelatedRefIds = !Array.isArray(relations.related_refids) && relatedRefIdsHasContent;
    if (!hasRelatedFeeds && !hasRelatedOids && !hasRelatedRefIds) {
      return undefined;
    }
    const typeMap = {
      related_feeds: "FEED_LIST" /* RelatedProductReferenceType.FEED_LIST */,
      related_oids: "PRODUCT_LIST" /* RelatedProductReferenceType.PRODUCT_LIST */,
      related_refids: "REFERENCE_LIST" /* RelatedProductReferenceType.REFERENCE_LIST */
    };
    const [key, value] = Object.entries(relations)[0];
    return {
      type: typeMap[key],
      references: value.split('/')
    };
  },
  hasMetaproducts(product) {
    const hasElementsOnMetaproducts = Boolean(product.metaproduct.children);
    const metaproductsIsEmpty = product.metaproduct.length === 0;
    return hasElementsOnMetaproducts && !metaproductsIsEmpty;
  },
  mapMetaproducts: (product, possibleColors) => {
    const metaproductData = product.metaproduct;
    const hasMetaproducts = Object.keys(metaproductData).length > 0;
    if (!hasMetaproducts) {
      return [];
    }
    const firstChild = metaproductData.children[0];
    const firstPlatform = APIv4MetaproductsAdapters.toCapitalize(metaproductData.children[0].platform ?? '');
    const hasPlatform = !!(firstPlatform && firstPlatform !== '');
    const hasEdition = !!(firstChild.edition && firstChild.edition !== '');
    const hasTwoMetaLevels = hasPlatform && hasEdition;
    const metaType = 'options';
    // This is only working if the priority is for platform and each platform has the same options
    if (hasTwoMetaLevels) {
      const metaproducts = [];
      // set all available colours
      const addedColours = [];
      let metaProductOptions = [];
      let metaproduct = {
        parentProduct: {
          id: product.productID
        },
        type: 'color',
        options: []
      };
      metaproductData.children.forEach(child => {
        const platform = APIv4MetaproductsAdapters.toCapitalize(child.platform ?? '');
        const edition = APIv4MetaproductsAdapters.toCapitalize(child.edition ?? '');
        if (!addedColours.includes(platform)) {
          addedColours.push(platform);
          metaProductOptions.push(APIv4GetProductsByFiltersResponseAdapter.mapMetaProductOptions(child, edition, platform));
        }
      });
      metaproduct.options = metaProductOptions;
      if (Object.keys(metaproduct.options).length > 0) {
        metaproducts.push(metaproduct);
      }
      // set all available options
      metaProductOptions = [];
      metaproduct = {
        parentProduct: {
          id: product.productID
        },
        type: 'options',
        options: []
      };
      metaproductData.children.forEach(child => {
        const platform = APIv4MetaproductsAdapters.toCapitalize(child.platform ?? '');
        const edition = APIv4MetaproductsAdapters.toCapitalize(child.edition ?? '');
        if (firstPlatform == platform) {
          //Only add the options for the first Colour
          metaProductOptions.push(APIv4GetProductsByFiltersResponseAdapter.mapMetaProductOptions(child, edition, platform));
        }
      });
      metaproduct.options = metaProductOptions;
      if (Object.keys(metaproduct.options).length > 0) {
        metaproducts.push(metaproduct);
      }
      return metaproducts;
    }
    const hasColorReferences = Object.keys(possibleColors).length !== 0;
    const hasMetaProductsColours = Object.keys(possibleColors).includes(firstPlatform);
    const metaProductOptions = [];
    const metaproduct = {
      parentProduct: {
        id: product.productID
      },
      type: metaType,
      options: []
    };
    // has Colours and ony have one level
    if (hasColorReferences && hasMetaProductsColours) {
      metaproduct.type = 'color';
      metaproductData.children.forEach(child => {
        const platform = child.platform ?? '';
        metaProductOptions.push(APIv4GetProductsByFiltersResponseAdapter.mapMetaProductOptions(child, platform, platform));
      });
    }
    // has options and ony have one level
    if (!hasMetaProductsColours && firstPlatform === '') {
      metaproduct.type = 'options';
      metaproductData.children.forEach(child => {
        const edition = child.edition ?? '';
        metaProductOptions.push(APIv4GetProductsByFiltersResponseAdapter.mapMetaProductOptions(child, edition));
      });
    }
    if (Object.keys(metaProductOptions).length > 0) {
      metaproduct.options = metaProductOptions;
    }
    return [metaproduct];
  },
  mapMetaProductOptions(child, label, color = '') {
    const metaproductOption = {
      label: APIv4MetaproductsAdapters.toCapitalize(label),
      id: child.productID,
      hasStock: Number(child.stock) > 0,
      stockPolicy: child.ostock,
      price: String(APIv4GetProductsByFiltersResponseAdapter.getChildMetaProductPrice(child.prices))
    };
    if (color !== '') {
      metaproductOption.color = APIv4MetaproductsAdapters.toCapitalize(color);
    }
    if (child.cf_kw_hexcode) {
      metaproductOption.hexcode = child.cf_kw_hexcode;
    }
    return metaproductOption;
  },
  getChildMetaProductPrice(prices) {
    let price = 0;
    prices.forEach(element => {
      if (element.bundle_object_id == '-1') {
        price = element.price;
      }
    });
    return price;
  },
  mapCustomFields(product) {
    if (!product.custom_fields) {
      return [];
    }
    let customFields = product.custom_fields;
    if (product.type == 'META' && product.custom_fields_meta) {
      customFields = product.custom_fields_meta;
    }
    const customFieldsFormatted = [];
    // for bundles physicals and digital products
    for (const key in customFields) {
      let value = customFields[key];
      if (Array.isArray(value)) {
        value = value[0];
      }
      customFieldsFormatted.push({
        type: key,
        value: value
      });
    }
    return customFieldsFormatted;
  },
  mapMetaproductCustomFields(product) {
    const metaproductData = product.metaproduct;
    const hasMetaproducts = Object.keys(metaproductData).length > 0;
    if (!hasMetaproducts) {
      return {};
    }
    const customFields = {};
    metaproductData.children.forEach(child => {
      const productId = Number(child.productID);
      customFields[productId] = [];
      for (const key in child) {
        if (key.toLowerCase().startsWith('cf_')) {
          customFields[productId].push({
            type: key,
            value: child[key]
          });
        }
      }
    });
    return customFields;
  },
  toProduct: (response, metaproductColors) => {
    const {
      response_data
    } = response;
    return APIv4GetProductsByFiltersResponseAdapter.toProductByFilters(response_data, metaproductColors);
  }
};
export default APIv4GetProductsByFiltersResponseAdapter;
